/* WP-10 routes (§14.3): public auth/legal + authed journal shell. */
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SessionProvider } from './auth/session';
import { useSession } from './auth/session-context';
import { StepUpProvider } from './auth/step-up';
import { AppShell } from './components/shell';
import { ActivatePage } from './pages/activate-page';
import { CollectDetailPage } from './pages/collect-detail-page';
import { CollectNewPage } from './pages/collect-new-page';
import { CollectPage } from './pages/collect-page';
import { HistoryPage, StatementMonthPage } from './pages/history-page';
import { HomePage } from './pages/home-page';
import { HumidorPage } from './pages/humidor-page';
import { LoginPage } from './pages/login-page';
import { HelpPage, LegalPage, MeRoutes } from './pages/me-pages';
import type { ReactNode } from 'react';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { state, customer } = useSession();
  const location = useLocation();
  if (state.status === 'loading') {
    return <div className="hp-main" aria-busy="true" />;
  }
  if (state.status !== 'authed') {
    // Preserve the full deep link (path + query + fragment) so post-login
    // navigation restores exactly what the member was viewing (FIN-04).
    const returnTo = location.pathname + location.search + location.hash;
    return <Navigate to={`/login?next=${encodeURIComponent(returnTo)}`} replace />;
  }
  // Owner-keyed boundary (FIN-01): when the authenticated owner's opaque scope
  // changes, the entire authenticated subtree remounts — rendered inventory,
  // in-memory selection, open sheets and pending page state from the previous
  // owner are dropped at once, and that owner's late responses land in an
  // unmounted tree. A same-owner refresh reuses the key, so drafts and
  // unknown-outcome recovery survive ordinary session reloads.
  return (
    <StepUpProvider key={customer?.ownerScope ?? 'none'}>{children}</StepUpProvider>
  );
}

export function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route
          path="/login"
          element={
            <main>
              <LoginPage />
            </main>
          }
        />
        <Route
          path="/activate"
          element={
            <main>
              <ActivatePage />
            </main>
          }
        />
        <Route
          path="/help"
          element={
            <main>
              <HelpPage backTo="/login" />
            </main>
          }
        />
        <Route
          path="/legal/:doc"
          element={
            <main>
              <LegalPage backTo="/login" />
            </main>
          }
        />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/" element={<HomePage />} />
          <Route path="/humidor" element={<HumidorPage />} />
          <Route path="/collect" element={<CollectPage />} />
          <Route path="/collect/new" element={<CollectNewPage />} />
          <Route path="/collect/:id" element={<CollectDetailPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/history/statement/:month" element={<StatementMonthPage />} />
          <Route path="/me/*" element={<MeRoutes />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SessionProvider>
  );
}
